import { getAuthClientConfigOrNull } from './config.js';

/**
 * `fetch` compartido de la familia de apps (extraído de speak, Fase 23).
 * Centraliza las dos únicas diferencias entre el cliente web y el nativo
 * (Capacitor), para que los wrappers de API de cada app no las conozcan:
 *
 * - **Web** (sin bridge nativo o `isNative === false`): ruta relativa
 *   same-origin + `credentials: 'include'` (la sesión viaja en la cookie
 *   httpOnly `orborix.session-token`; en dev el proxy de Vite la hace
 *   same-origin contra el backend :9000).
 * - **Nativo** (bridge con `isNative === true`): antepone `apiBaseUrl()`
 *   (base absoluta, fail-fast si falta), manda `Authorization: Bearer <token>`
 *   y el header `X-Orborix-Native: 1` (el backend entonces devuelve el token
 *   en el body de los endpoints de auth).
 *
 * Cada wrapper sigue siendo dueño de su parseo de respuesta/errores; este
 * helper sólo resuelve **base URL + credenciales + auth**. Ver
 * `docs/speak-plan/23-mobile-capacitor.md` §3.2/§3.3.
 */

/**
 * Token de sesión para la app nativa. En **web** es siempre `null` (la sesión
 * viaja en la cookie httpOnly same-origin y no se necesita Bearer). En
 * **nativo** lo setea el login y el boot tras leerlo del storage seguro. Vive
 * **en memoria** a propósito: la persistencia durable es responsabilidad del
 * bridge nativo de la app (`NativeAuthBridge`).
 */
let authToken: string | null = null;

/** Setea (o limpia con `null`) el Bearer token de la sesión nativa. */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/** Token actual de la sesión nativa (`null` en web o sin login). */
export function getAuthToken(): string | null {
  return authToken;
}

/**
 * `fetch` envuelto: resuelve base URL absoluta (nativo) + credenciales +
 * Bearer. Los wrappers le pasan rutas relativas (`/api/...`).
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // Bridge nativo OPCIONAL: sin config (o sin bridge) ⇒ camino web same-origin.
  const native = getAuthClientConfigOrNull()?.native;
  const isNative = native?.isNative === true;
  const url = isNative ? `${native.apiBaseUrl()}${path}` : path;
  const headers = new Headers(init.headers);
  if (isNative) {
    headers.set('X-Orborix-Native', '1');
    if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
  }
  // `credentials: 'include'` primero para que un wrapper pueda overridearlo vía
  // `init`; `headers` (Headers ya armado) va al final para ganarle al `init.headers` crudo.
  return fetch(url, { credentials: 'include', ...init, headers });
}
